// Meta Pixel provider. Installs the standard fbq stub synchronously so calls
// made before the script finishes loading are queued and replayed by the
// pixel script. The loader fires the initial PageView (mirroring Meta's
// canonical snippet); subsequent SPA navigations come from the trackPageView
// effect.

import { AnalyticsEventPayload } from '@/lib/tracking/analytics-events';


// Shape of the global `fbq` stub Meta's snippet installs. It is callable
// (as a function) AND carries auxiliary fields the Meta SDK reads when it
// finally loads (`callMethod` to dispatch eagerly, `queue` to buffer until
// then). Modeling it as a callable-with-properties type lets us avoid `any`.
type FbqFn = ((...args: unknown[]) => void) & {
	callMethod?: (...args: unknown[]) => void;
	queue: unknown[];
	push: FbqFn;
	loaded: boolean;
	version: string;
};

declare global {
	interface Window {
		fbq?: FbqFn;
		_fbq?: FbqFn;
	}
}


// Track which pixel IDs have already been initialized so a settings change
// in the same session doesn't re-init and double-fire.
const initializedPixelIds = new Set<string>();


// Returns the installed fbq (existing or freshly created), or null in SSR.
const installFbqStub = (): FbqFn | null => {
	if(typeof window === 'undefined') { return null; }
	if(window.fbq) { return window.fbq; }

	// Mirrors Meta's canonical fbq polyfill: when the SDK arrives it replaces
	// `callMethod` with a real implementation; until then, calls are queued.
	const fbq = function fbqStub(...args: unknown[]) {
		if(fbq.callMethod) {
			fbq.callMethod(...args);
		} else {
			fbq.queue.push(args);
		}
	} as FbqFn;
	fbq.push = fbq;
	fbq.loaded = true;
	fbq.version = '2.0';
	fbq.queue = [];

	window.fbq = fbq;
	if(!window._fbq) { window._fbq = fbq; }
	return fbq;
};


export const loadMetaPixel = (pixelId: string | null | undefined): void => {

	if(!pixelId) { return; }
	if(typeof document === 'undefined') { return; }
	if(initializedPixelIds.has(pixelId)) { return; }

	const fbq = installFbqStub();
	if(!fbq) { return; }

	const id = `meta-pixel-js-${pixelId}`;
	if(!document.getElementById(id)) {
		const script = document.createElement('script');
		script.id = id;
		script.src = 'https://connect.facebook.net/en_US/fbevents.js';
		script.async = true;
		document.head.appendChild(script);
	}

	fbq('init', pixelId);
	// Fire an initial PageView on init so we capture the page the script first
	// loaded on. Subsequent navigations flow through trackPageView.
	fbq('track', 'PageView');
	initializedPixelIds.add(pixelId);
};


export const dispatchMeta = (eventName: string, payload: AnalyticsEventPayload): void => {
	if(typeof window === 'undefined' || !window.fbq) { return; }
	window.fbq('track', eventName, payload);
};


// For tests — clears the in-module load registry.
export const __resetMetaPixelForTests = (): void => {
	initializedPixelIds.clear();
};
